import { ref, provide, computed } from "vue";
import { useStore } from "vuex";
import { options, columns } from "@/helpers/hardwareReportTableHelper";
import { groupBy } from "@/helpers/sharedHelper";

export default function useHardwareReportTable() {
  const store = useStore();

  // We expose this ref to be bound by the component
  const archiveDataTableRef = ref(null);

  // This computed is still useful for the CSV export button
  //   const deviceQuery = computed(() => store.getters["devices/getDeviceQuery"]);

  const tableOptions = {
    ...options,
    pageLength: store.getters["devices/getDeviceQuery"].length, // Get initial page length

    // --- Server-Side Processing Setup ---
    processing: true, // Shows "Processing..."
    serverSide: true, // This is the most important option

    // The ajax function is now the data source
    ajax: async (data, callback) => {
      console.log("DataTables is requesting data:", data);

      // 1. Map DataTables request data to your Vuex query format
      const order = data.order && data.order.length > 0 ? data.order[0] : null;
      let orderBy = "id";
      let dir = "asc";

      if (order) {
        // Check if column is orderable
        if (columns[order.column] && columns[order.column].data) {
          orderBy = columns[order.column].data;
          dir = order.dir;
        }
      }

      const newQuery = {
        draw: data.draw,
        start: data.start,
        length: data.length,
        search: data.search ? data.search.value : "",
        orderBy: orderBy,
        dir: dir,
      };

      // 2. Commit this query to the store
      // This keeps the store in sync (e.g., for the export button)
      store.commit("devices/SET_DEVICE_QUERY", newQuery);

      try {
        // 3. Dispatch the Vuex action (the mock one)
        // Since the component title is "DELETED DEVICES", we call fetchArchivedDevices
        const response = await store.dispatch("devices/fetchArchivedDevices");

        // 4. Pass the mock response to the DataTables callback
        callback({
          draw: response.draw,
          recordsTotal: response.recordsTotal,
          recordsFiltered: response.recordsFiltered,
          data: response.data,
        });
      } catch (error) {
        console.error("Error fetching data for DataTables:", error);
        // Tell DataTables an error occurred
        callback({
          draw: data.draw,
          recordsTotal: 0,
          recordsFiltered: 0,
          data: [],
          error: "Could not load data.",
        });
      }
    },
    // --- End Server-Side Processing Setup ---

    buttons: [
      function () {
        return {
          extend: "csvHtml5",
          text: "CSV",
          title: "Central",
          exportOptions: {
            columns: [
              0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18,
            ],
          },
          customizeData: (data) => {
            const dt = this;
            const selectedRows = dt.rows({ selected: true }).data().toArray();
            if (selectedRows?.length > 0) {
              data.body = selectedRows.map((row) => Object.values(row));
            } else {
              // This part is problematic for server-side.
              // We'll just export the current page's data.
              // For a full export, a different backend endpoint is needed.
              const allRows = dt.rows().data().toArray();
              data.body = allRows.map((row) => Object.values(row));
              console.warn(
                "CSV Export is only exporting the current page. For full export, a dedicated backend call is needed."
              );
            }
          },
        };
      },
    ],
  };

  // --- Selection logic remains the same ---
  const selectedDevices = ref([]);
  const selectedDevicesByCourseName = computed(() => {
    return groupBy(selectedDevices.value, "course_name");
  });

  provide("selectedDevices", selectedDevices);
  provide("selectedDevicesByCourseName", selectedDevicesByCourseName);

  const onSelect = ({ dt }) => {
    const selectedRows = dt.rows({ selected: true }).data();
    selectedDevices.value = selectedRows.toArray();
  };

  const onDeselect = ({ dt }) => {
    const deSelectedRows = dt.rows({ selected: true }).data();
    selectedDevices.value = deSelectedRows.toArray();
  };
  // --- End Selection logic ---

  const resetTable = () => {
    store.commit("devices/RESET_DEVICE_QUERY");
    // Now that we have a ref, we can tell DataTables to reload
    if (archiveDataTableRef.value) {
      const dt = archiveDataTableRef.value.dt;
      if (dt) {
        dt.ajax.reload(); // Tell DataTables to re-fetch data
      }
    }
  };

  return {
    // tableData is no longer needed
    columns,
    tableOptions,
    onSelect,
    onDeselect,
    resetTable,
    archiveDataTableRef, // Expose the ref
  };
}
